import {
  Breakpoints,
  DesignTokenSizeEnum,
  GalleryContext,
  GallerySlide,
} from '@volkswagen-onehub/components-core';
import { VehicleCardsContainerWrapper } from '../VehicleCardsContainer/VehicleCardsContainer.styles';
import { CarouselNavigation } from '@tribal-worldwide-london/ui-component-library-vwg';
import VehicleCard, { type VehicleCardProps } from '../VehicleCard/VehicleCard';
import useResizeResponsive from '@/hooks/useResizeResponsive';
import { isMobile } from '@/modules/utils/breakpoints';
import Box from '../Box/Box';
import { getSlideOffsetY } from '../VehicleCardsContainer/VehicleCardsContainer';
import { useContext, useEffect } from 'react';
import Carousel from '../Carousel/Carousel';

interface VehicleCardsCarouselProps {
  activeSlideIndex?: number;
  vehicles: VehicleCardProps[];
  collapsed?: boolean;
  onSlideChange?: (activeSlideIndex: number) => void;
  resetSlides?: boolean;
}

export enum AriaLabels {
  CAROUSEL_ARIA_LABEL = 'Vehicle cards carousel',
  NAVIGATION_ARIA_LABEL = 'Navigation for vehicle toggle button carousel',
}

const VehicleCardsCarouselNavigationBullets = ({
  vehicles,
  activeSlideIndex = 0,
  resetSlides,
}: VehicleCardsCarouselProps) => {
  const galleryContext = useContext(GalleryContext);

  useEffect(() => {
    galleryContext.triggerChange(activeSlideIndex);
  }, [activeSlideIndex]);

  useEffect(() => {
    if (resetSlides) galleryContext.triggerChange(0);
  }, [resetSlides]);

  return <CarouselNavigation aria-label={AriaLabels.NAVIGATION_ARIA_LABEL} />;
};

function VehicleCardsCarousel({
  onSlideChange,
  vehicles,
  activeSlideIndex,
  collapsed,
  resetSlides,
}: VehicleCardsCarouselProps) {
  const isMobileWidth = useResizeResponsive(isMobile);

  const slideOffsetY = getSlideOffsetY(vehicles);

  return (
    <VehicleCardsContainerWrapper>
      <Carousel
        id="vehicle-cards-carousel"
        bottomElements={
          <VehicleCardsCarouselNavigationBullets
            vehicles={vehicles}
            activeSlideIndex={activeSlideIndex}
            resetSlides={resetSlides}
          />
        }
        aria-label={AriaLabels.CAROUSEL_ARIA_LABEL}
        slidesPerPage={{
          [Breakpoints.default]: 2,
          [Breakpoints.b960]: 3,
        }}
        gutter={
          isMobileWidth
            ? DesignTokenSizeEnum.static150
            : DesignTokenSizeEnum.static300
        }
        handleChange={onSlideChange}
      >
        {vehicles.map((vehicle) => (
          <GallerySlide
            key={vehicle.title}
            aria-labelledby={AriaLabels.CAROUSEL_ARIA_LABEL}
          >
            <Box pt={slideOffsetY} width="100%">
              <VehicleCard {...vehicle} collapsed={collapsed} />
            </Box>
          </GallerySlide>
        ))}
      </Carousel>
    </VehicleCardsContainerWrapper>
  );
}

export default VehicleCardsCarousel;
